import posthog, { posthogEnabled } from './posthog';
import { logError, setLogUser } from './frontendLog';

// Thin analytics facade over PostHog so the rest of the app has one place to
// identify users, capture events, and reset on logout. All calls are safe
// no-ops when PostHog isn't configured (no REACT_APP_POSTHOG_KEY).

export type AppUser = {
  userId?: string | null;
  email?: string | null;
  username?: string | null;
  userRole?: string | null;
  appversion?: string | null;
  lastLogin?: string | null;
};

// Person properties attached to the user's PostHog profile + every event and
// session replay, so all activity is tied to a real person.
const toPersonProps = (user: AppUser) => ({
  name: user.username || undefined, // full name (falls back to username)
  username: user.username || undefined,
  email: user.email || undefined,
  role: user.userRole ?? undefined,
  app_version: user.appversion || undefined,
  last_login: user.lastLogin || undefined
});

// Identify the signed-in user. distinct_id is the stable userId.
export const identifyUser = (user: AppUser) => {
  // Tag the activity log first, and unconditionally: it must attribute entries
  // to a person even when PostHog is disabled, and it is the one sink that keeps
  // working while the API is down — so this must not sit behind the early return.
  setLogUser({
    userId: user?.userId,
    username: user?.username,
    email: user?.email,
    role: user?.userRole
  });
  if (!posthogEnabled || !user?.userId) return;
  const props = toPersonProps(user);
  posthog.identify(String(user.userId), props);
  // identify() writes distinct_id to localStorage synchronously but only *queues*
  // the $identify request, and it emits nothing at all when persistence already
  // holds this distinct_id. So if a login's $identify is lost before it flushes
  // (e.g. the hard reload on the version-changed path in login2), no later
  // identify() will ever retry it, and the person keeps the name/email/role of
  // whatever profile already existed. setPersonProperties always emits a $set,
  // so the profile is created or corrected either way.
  posthog.setPersonProperties(props);
};

// Re-identify from localStorage whenever a signed-in user re-opens the app,
// so returning sessions are attributed without needing a fresh login.
export const identifyFromStorage = () => {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  if (!posthogEnabled || !userId || !token) return;
  identifyUser({
    userId,
    email: localStorage.getItem('email'),
    username: localStorage.getItem('username'),
    userRole: localStorage.getItem('userRole'),
    appversion: localStorage.getItem('appversion'),
    lastLogin: localStorage.getItem('lastLogin')
  });
};

// Events queued normally are batched and can be lost when the caller then tears
// the page down (window.location.assign / replace). `instant` bypasses the queue
// and hands the payload to sendBeacon, which survives unload.
const INSTANT = {
  send_instantly: true,
  transport: 'sendBeacon'
} as const;

export const captureEvent = (
  event: string,
  properties?: Record<string, any>,
  options?: { instant?: boolean }
) => {
  if (!posthogEnabled) return;
  posthog.capture(event, properties, options?.instant ? { ...INSTANT } : undefined);
};

// Report a caught error to PostHog Error Tracking.
//
// Use this in every catch block that would otherwise swallow the failure:
// console.* is stubbed out in production (see App.tsx), so a console log there
// is invisible, and a bare `catch {}` leaves no trace at all.
export const captureError = (
  context: string,
  error: unknown,
  properties?: Record<string, any>
) => {
  const err = error instanceof Error ? error : new Error(String(error));
  // Also to the durable activity log, and BEFORE the posthogEnabled check below.
  // Every one of this function's ~34 call sites is a catch block, and they used to
  // report to PostHog alone — so a caught error appeared in a dashboard but never
  // in the per-user file you actually read during an incident, and vanished
  // entirely whenever PostHog was disabled or unreachable.
  logError('caught_error', {
    msg: String(err.message || err),
    // The context string is the call site, e.g. "content/authentication/login2:345".
    reason: context,
    extra: { stack: String(err.stack || ''), ...(properties || {}) }
  });
  if (!posthogEnabled) return;
  posthog.captureException(err, { context, ...properties });
};

// A failed API call. Deliberately records only url/method/status — never
// `config.params`, which carries the auth token and userId.
export const captureApiFailure = (details: {
  url?: string;
  method?: string;
  status?: number;
  code?: string;
  retries?: number;
  reason: 'client_error' | 'retries_exhausted' | 'no_response' | 'unretriable';
}) => {
  if (!posthogEnabled) return;
  posthog.capture('api_request_failed', details);
};

// Why a logout happened - lets us tell a deliberate sign-out from an
// expired/invalid session.
export type LogoutReason = 'manual' | 'idle_timeout' | 'session_expired';

export const captureLogout = (reason: LogoutReason) => {
  if (!posthogEnabled) return;
  // Always instant: every caller wipes storage and/or hard-navigates straight
  // after this, which would otherwise discard the queued event.
  posthog.capture('user_logged_out', { reason }, { ...INSTANT });
  // Stop tying subsequent (anonymous) activity to this person.
  posthog.reset();
};

export default posthog;
